// Simple molecular properties calculation
import { Molecule, ELEMENTS, MolecularProperties } from '../types/molecule';

export function calculateMolecularProperties(molecule: Molecule): MolecularProperties {
  let mw = 0;
  const atomComposition: Record<string, number> = {};
  
  for (const atom of molecule.atoms) {
    const elem = ELEMENTS[atom.element];
    mw += elem?.atomicMass || 12;
    atomComposition[atom.element] = (atomComposition[atom.element] || 0) + 1;
  }
  
  let hba = 0, hbd = 0;
  for (const atom of molecule.atoms) {
    if (atom.element === 'O' || atom.element === 'N') hba++;
    if ((atom.element === 'O' || atom.element === 'N') && hasHydrogen(molecule, atom.id)) hbd++;
  }
  
  const rotatable = molecule.bonds.filter(b => {
    const a1 = molecule.atoms[b.atomIndex1];
    const a2 = molecule.atoms[b.atomIndex2];
    if (a1.element === 'H' || a2.element === 'H') return false;
    if (b.order > 1) return false;
    return true;
  }).length;
  
  let logp = 0;
  const logpContrib: Record<string, number> = { C: 0.54, H: 0.23, O: -1.0, N: -1.0, S: -0.43, F: 0.37, Cl: 0.71 };
  for (const atom of molecule.atoms) {
    logp += logpContrib[atom.element] || 0;
  }
  
  let tpsa = 0;
  const tpsaContrib: Record<string, number> = { O: 12.28, N: 15.79, S: 25.45, P: 13.29 };
  for (const atom of molecule.atoms) {
    tpsa += tpsaContrib[atom.element] || 0;
  }
  
  let rings = 0;
  const visited = new Set<number>();
  for (let i = 0; i < molecule.atoms.length; i++) {
    if (!visited.has(i)) {
      visited.add(i);
      let hasCycle = false;
      for (const b of molecule.bonds) {
        if ((b.atomIndex1 === i || b.atomIndex2 === i)) {
          const neighbor = b.atomIndex1 === i ? b.atomIndex2 : b.atomIndex1;
          if (!visited.has(neighbor)) {
            hasCycle = true;
            break;
          }
        }
      }
      if (hasCycle) rings++;
    }
  }
  
  let violations = 0;
  if (mw > 500) violations++;
  if (logp > 5) violations++;
  if (hbd > 5) violations++;
  if (hba > 10) violations++;
  
  let drugLikenessClass: 'drug-like' | 'borderline' | 'non-drug-like' = 'non-drug-like';
  let drugLikenessScore = 0;
  
  if (violations === 0) {
    drugLikenessClass = 'drug-like';
    drugLikenessScore = 100;
  } else if (violations === 1) {
    drugLikenessClass = 'borderline';
    drugLikenessScore = 60;
  } else {
    drugLikenessClass = 'non-drug-like';
    drugLikenessScore = Math.max(0, 40 - violations * 15);
  }
  
  // Calculate formula from composition
  let formula = '';
  const sortedElements = Object.keys(atomComposition).sort((a, b) => {
    const order = ['C', 'H', 'N', 'O', 'F', 'P', 'S', 'Cl', 'Br', 'I'];
    return order.indexOf(a) - order.indexOf(b);
  });
  
  for (const element of sortedElements) {
    formula += element;
    if (atomComposition[element] > 1) {
      formula += atomComposition[element].toString();
    }
  }
  
  return {
    molecularWeight: mw,
    exactMass: mw,
    formula,
    atomCount: molecule.atoms.length,
    bondCount: molecule.bonds.length,
    ringCount: rings,
    rotatableBondCount: rotatable,
    hydrogenBondDonorCount: hbd,
    hydrogenBondAcceptorCount: hba,
    topologicalPolarSurfaceArea: tpsa,
    logP: logp,
    formalCharge: 0,
    heavyAtomCount: molecule.atoms.filter(a => a.element !== 'H').length,
    carbonAtomCount: atomComposition['C'] || 0,
    nitrogenAtomCount: atomComposition['N'] || 0,
    oxygenAtomCount: atomComposition['O'] || 0,
    sulfurAtomCount: atomComposition['S'] || 0,
    halogenCount: (atomComposition['F'] || 0) + (atomComposition['Cl'] || 0) + (atomComposition['Br'] || 0) + (atomComposition['I'] || 0),
    aromaticRingCount: 0,
    saturatedRingCount: rings,
    heteroatomCount: Object.keys(atomComposition).filter(e => e !== 'C' && e !== 'H').length,
    ruleOfFiveViolations: violations,
    drugLikenessScore,
    drugLikenessClass,
    atomComposition,
  };
}

function hasHydrogen(molecule: Molecule, atomId: string): boolean {
  return molecule.bonds.some(b => {
    const neighbor = b.atomIndex1 === parseInt(atomId) ? b.atomIndex2 : b.atomIndex2 === parseInt(atomId) ? b.atomIndex1 : -1;
    return neighbor !== -1 && molecule.atoms[neighbor]?.element === 'H';
  });
}

// Alias for backward compatibility
export { calculateMolecularProperties as calculateProperties };

